import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';

const EmailComposer = ({ onContentUpdate }) => {
  const [emailContent, setEmailContent] = useState({
    template: '',
    content: `Dear {{contact_name}},\n\nI hope this email finds you well. I'm reaching out to share exciting news about our latest publication from PenGame Publishing.\n\n{{book_title}} by {{author_name}} is now available, and I believe it would be of great interest to your readers.\n\nKey highlights:\n• {{book_genre}} that explores {{main_theme}}\n• Available in hardcover, paperback, and digital formats\n• Author available for interviews and book signings\n\nI've attached the press kit with high-resolution images, author bio, and sample chapters for your review.\n\nWould you be interested in featuring this book or interviewing the author? I'm happy to provide additional materials or arrange a conversation.\n\nBest regards,\n{{sender_name}}\n{{sender_title}}\nPenGame Publishing`,
    attachments: []
  });

  const [showMergeFields, setShowMergeFields] = useState(false);
  const [activeTab, setActiveTab] = useState('compose');
  const fileInputRef = useRef(null);

  const templateOptions = [
    { value: '', label: 'Start from scratch' },
    { value: 'book-launch', label: 'Book Launch Announcement' },
    { value: 'author-interview', label: 'Author Interview Request' },
    { value: 'review-request', label: 'Book Review Request' },
    { value: 'event-invitation', label: 'Event Invitation' },
    { value: 'follow-up', label: 'Follow-up Template' }
  ];

  const mergeFields = [
    { field: '{{contact_name}}', description: 'Recipient\'s name' },
    { field: '{{contact_outlet}}', description: 'Media outlet name' },
    { field: '{{book_title}}', description: 'Book title' },
    { field: '{{author_name}}', description: 'Author name' },
    { field: '{{book_genre}}', description: 'Book genre' },
    { field: '{{publication_date}}', description: 'Publication date' },
    { field: '{{sender_name}}', description: 'Your name' },
    { field: '{{sender_title}}', description: 'Your title' }
  ];

  const mockAttachments = [
    { id: 1, name: 'Press_Kit_Complete.pdf', size: '2.4 MB', type: 'pdf' },
    { id: 2, name: 'Author_Headshot_HighRes.jpg', size: '1.8 MB', type: 'image' },
    { id: 3, name: 'Book_Cover_300DPI.png', size: '3.2 MB', type: 'image' }
  ];

  const handleTemplateChange = (templateId) => {
    setEmailContent(prev => ({ ...prev, template: templateId }));
    
    const templates = {
      'book-launch': `Dear {{contact_name}},\n\nI'm excited to announce the launch of {{book_title}} by {{author_name}}, now available from PenGame Publishing.\n\nThis {{book_genre}} offers readers {{main_theme}} and has already garnered attention from early reviewers.\n\nPress materials and review copies are available upon request.\n\nBest regards,\n{{sender_name}}`,
      'author-interview': `Hello {{contact_name}},\n\nI hope you're doing well. I'm reaching out to see if you'd be interested in interviewing {{author_name}}, author of the new {{book_genre}} {{book_title}}.\n\n{{author_name}} has a fascinating background and would make an excellent guest for {{contact_outlet}}.\n\nWould you be available for a brief conversation about this opportunity?\n\nBest,\n{{sender_name}}`,
      'review-request': `Dear {{contact_name}},\n\nI hope this message finds you well. I'm writing to offer you an advance review copy of {{book_title}} by {{author_name}}.\n\nGiven {{contact_outlet}}'s excellent coverage of {{book_genre}}, I believe this book would resonate with your audience.\n\nI'd be happy to send a digital or physical copy for your consideration.\n\nThank you for your time,\n{{sender_name}}`
    };

    if (templates?.[templateId]) {
      setEmailContent(prev => ({ ...prev, content: templates?.[templateId] }));
    }
  };

  const handleContentChange = (content) => {
    setEmailContent(prev => ({ ...prev, content }));
    onContentUpdate?.(content);
  };

  const insertMergeField = (field) => {
    const textarea = document.getElementById('email-content');
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const newContent = emailContent?.content?.substring(0, start) + field + emailContent?.content?.substring(end);
    handleContentChange(newContent);
    setShowMergeFields(false);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event?.target?.files);
    const newAttachments = files?.map(file => ({
      id: Date.now() + Math.random(),
      name: file?.name,
      size: `${(file?.size / 1024 / 1024)?.toFixed(1)} MB`,
      type: file?.type?.includes('image') ? 'image' : file?.type?.includes('pdf') ? 'pdf' : 'file'
    }));
    
    setEmailContent(prev => ({
      ...prev,
      attachments: [...prev?.attachments, ...newAttachments]
    }));
  };

  const removeAttachment = (attachmentId) => {
    setEmailContent(prev => ({
      ...prev,
      attachments: prev?.attachments?.filter(att => att?.id !== attachmentId)
    }));
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return 'FileText';
      case 'image': return 'Image';
      default: return 'File';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-secondary rounded-md flex items-center justify-center">
              <Icon name="Mail" size={16} color="white" />
            </div>
            <h2 className="text-lg font-heading font-semibold text-foreground">Email Composer</h2>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm" iconName="Save" iconPosition="left">
              Save Draft
            </Button>
            <Button variant="outline" size="sm" iconName="Eye" iconPosition="left">
              Preview
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mt-4">
          {[
            { id: 'compose', label: 'Compose', icon: 'Edit3' },
            { id: 'template', label: 'Templates', icon: 'Layout' },
            { id: 'attachments', label: 'Attachments', icon: 'Paperclip' }
          ]?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="p-4">
        {activeTab === 'compose' && (
          <div className="space-y-4">
            {/* Template Selection */}
            <Select
              label="Start with Template"
              options={templateOptions}
              value={emailContent?.template}
              onChange={handleTemplateChange}
              placeholder="Choose a template or start from scratch"
            />

            {/* Formatting Toolbar */}
            <div className="flex items-center space-x-2 p-2 bg-muted rounded-md">
              <Button variant="ghost" size="sm" iconName="Bold">
                <span className="sr-only">Bold</span>
              </Button>
              <Button variant="ghost" size="sm" iconName="Italic">
                <span className="sr-only">Italic</span>
              </Button>
              <Button variant="ghost" size="sm" iconName="Underline">
                <span className="sr-only">Underline</span>
              </Button>
              <div className="w-px h-6 bg-border" />
              <Button variant="ghost" size="sm" iconName="Link">
                <span className="sr-only">Link</span>
              </Button>
              <Button variant="ghost" size="sm" iconName="List">
                <span className="sr-only">List</span>
              </Button>
              <div className="w-px h-6 bg-border" />
              <Button 
                variant="ghost" 
                size="sm" 
                iconName="Code" 
                onClick={() => setShowMergeFields(!showMergeFields)}
              >
                <span className="sr-only">Merge Fields</span>
              </Button>
            </div>

            {/* Merge Fields Panel */}
            {showMergeFields && (
              <div className="bg-muted rounded-md p-4">
                <h4 className="text-sm font-medium text-foreground mb-3">Available Merge Fields</h4>
                <div className="grid grid-cols-2 gap-2">
                  {mergeFields?.map((field) => (
                    <button
                      key={field?.field}
                      onClick={() => insertMergeField(field?.field)}
                      className="text-left p-2 rounded border border-border bg-card hover:bg-muted transition-colors"
                    >
                      <div className="text-sm font-mono text-primary">{field?.field}</div>
                      <div className="text-xs text-muted-foreground">{field?.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Email Content */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Email Content</label>
              <textarea
                id="email-content"
                value={emailContent?.content}
                onChange={(e) => handleContentChange(e?.target?.value)}
                className="w-full h-64 p-3 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Compose your email message..."
              />
              <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>{emailContent?.content?.length} characters</span>
                <span>Estimated read time: {Math.ceil(emailContent?.content?.split(' ')?.length / 200)} min</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'template' && (
          <div className="space-y-4">
            <div className="text-center py-8">
              <Icon name="Layout" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Template Library</h3>
              <p className="text-muted-foreground mb-4">Choose from pre-built email templates</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templateOptions?.slice(1)?.map((template) => (
                  <button
                    key={template?.value}
                    onClick={() => handleTemplateChange(template?.value)}
                    className="p-4 border border-border rounded-lg hover:bg-muted transition-colors text-left"
                  >
                    <div className="font-medium text-foreground">{template?.label}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Professional template for {template?.label?.toLowerCase()}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'attachments' && (
          <div className="space-y-4">
            {/* Upload Area */}
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-foreground font-medium mb-1">Drop files here or click to upload</p>
              <p className="text-sm text-muted-foreground mb-4">PDF, JPG, PNG up to 10MB each</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef?.current?.click()}
                iconName="Plus" 
                iconPosition="left"
              >
                Add Files
              </Button>
            </div>

            {/* Current Attachments */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">
                Attached Files ({emailContent?.attachments?.length + mockAttachments?.length})
              </h4>
              <div className="space-y-2">
                {[...mockAttachments, ...emailContent?.attachments]?.map((attachment) => (
                  <div key={attachment?.id} className="flex items-center justify-between p-3 border border-border rounded-md">
                    <div className="flex items-center space-x-3">
                      <Icon name={getFileIcon(attachment?.type)} size={16} className="text-muted-foreground" />
                      <div>
                        <div className="text-sm font-medium text-foreground">{attachment?.name}</div>
                        <div className="text-xs text-muted-foreground">{attachment?.size}</div>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeAttachment(attachment?.id)}
                      iconName="X"
                    >
                      <span className="sr-only">Remove</span>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailComposer;